/**
 * ImportPreviewCard - Batch import preview for Guided Import (Phase 3b-3)
 *
 * Rendered when a message contains an [[IMPORT_PREVIEW:batchId:title:count]] token.
 * Shows a summary of all items in the import batch with:
 *  - Title and entity-type stats (N Days · N Places · N Accommodation · N Travel)
 *  - "Accept All" button — approves all outstanding proposals in the batch at once
 *  - "Review Each" toggle — expands the list with individual ProposalCards
 *  - "Dismiss All" button — dismisses all outstanding proposals in the batch
 *  - Progress indicator when some proposals have already been actioned
 *
 * Props:
 *   batchId      - The batchId that all proposals in this batch share
 *   title        - Tour/trip title extracted from the import source
 *   itemCount    - Approximate count shown in the header (from token; use proposals.length for actual)
 *   suggestions  - ALL agentSuggestions for the trip (this component filters by batchId)
 *   onApprove    - (suggestionId) => void
 *   onDismiss    - (suggestionId) => void
 *   onModify     - (suggestionId, note) => void
 *   canApprove   - boolean; hide action buttons when false (shared-trip viewer)
 */

import React, { useState, useMemo } from 'react';
import ProposalCard from '../ProposalCard/ProposalCard';
import './ImportPreviewCard.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getEntityIcon(entityType) {
  switch ((entityType || '').toLowerCase()) {
    case 'accommodation': return '🏨';
    case 'travel': return '🚆';
    case 'place': return '📍';
    case 'day': return '📅';
    default: return '💡';
  }
}

function getEntityLabel(entityType) {
  switch ((entityType || '').toLowerCase()) {
    case 'accommodation': return 'Accommodation';
    case 'travel': return 'Travel';
    case 'place': return 'Place';
    case 'day': return 'Day';
    default: return 'Item';
  }
}

// ---------------------------------------------------------------------------
// ImportPreviewCard Component
// ---------------------------------------------------------------------------

const ImportPreviewCard = ({
  batchId,
  title,
  itemCount,
  suggestions = [],
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const [reviewMode, setReviewMode] = useState(false);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  const [isDismissingAll, setIsDismissingAll] = useState(false);

  // Filter suggestions to only those in this batch
  const batchSuggestions = useMemo(
    () => (Array.isArray(suggestions) ? suggestions.filter((s) => s.batchId === batchId) : []),
    [suggestions, batchId]
  );

  const outstanding = batchSuggestions.filter((s) => s.status === 'outstanding');
  const approved = batchSuggestions.filter((s) => s.status === 'approved');
  const dismissed = batchSuggestions.filter((s) => s.status === 'dismissed');
  const actioned = approved.length + dismissed.length;
  const total = batchSuggestions.length;

  // Entity type counts from the batch
  const stats = useMemo(() => {
    const counts = { day: 0, place: 0, accommodation: 0, travel: 0 };
    batchSuggestions.forEach((s) => {
      const et = (s.action?.entityType || '').toLowerCase();
      if (counts.hasOwnProperty(et)) counts[et]++;
    });
    return counts;
  }, [batchSuggestions]);

  const statParts = [
    stats.day > 0 && `${stats.day} Day${stats.day !== 1 ? 's' : ''}`,
    stats.place > 0 && `${stats.place} Place${stats.place !== 1 ? 's' : ''}`,
    stats.accommodation > 0 && `${stats.accommodation} Accommodation`,
    stats.travel > 0 && `${stats.travel} Travel leg${stats.travel !== 1 ? 's' : ''}`,
  ].filter(Boolean);

  // Use actual proposal count if loaded; fall back to tokenised count
  const displayCount = total > 0 ? total : (parseInt(itemCount) || 0);

  const handleAcceptAll = async () => {
    if (!canApprove || outstanding.length === 0 || isAcceptingAll) return;
    setIsAcceptingAll(true);
    try {
      for (const s of outstanding) {
        if (typeof onApprove === 'function') {
          await onApprove(s.id);
        }
      }
    } finally {
      setIsAcceptingAll(false);
    }
  };

  const handleDismissAll = async () => {
    if (!canApprove || outstanding.length === 0 || isDismissingAll) return;
    setIsDismissingAll(false);
    try {
      for (const s of outstanding) {
        if (typeof onDismiss === 'function') {
          await onDismiss(s.id);
        }
      }
    } finally {
      setIsDismissingAll(false);
    }
  };

  const allActioned = total > 0 && actioned >= total;
  const allApproved = total > 0 && approved.length >= total;

  return (
    <div className="import-preview-card" role="region" aria-label={`Import Preview: ${title}`}>
      {/* Header */}
      <div className="import-preview-card__header">
        <div className="import-preview-card__icon-area">
          <span className="import-preview-card__icon" aria-hidden="true">📥</span>
        </div>
        <div className="import-preview-card__title-area">
          <div className="import-preview-card__title">{title || 'Import Preview'}</div>
          <div className="import-preview-card__stats">
            {statParts.length > 0
              ? statParts.join(' · ')
              : `${displayCount} item${displayCount !== 1 ? 's' : ''}`}
          </div>
        </div>
      </div>

      {/* Progress bar (shown once some items have been actioned) */}
      {total > 0 && actioned > 0 && !allActioned && (
        <div className="import-preview-card__progress">
          <div className="import-preview-card__progress-bar">
            <div
              className="import-preview-card__progress-fill"
              style={{ width: `${Math.round((actioned / total) * 100)}%` }}
            />
          </div>
          <span className="import-preview-card__progress-label">
            {actioned} of {total} items actioned
          </span>
        </div>
      )}

      {/* All done state */}
      {allActioned && (
        <div className="import-preview-card__done">
          {allApproved
            ? '✅ All items have been approved and added to your trip.'
            : `✅ Import complete — ${approved.length} approved, ${dismissed.length} dismissed.`}
        </div>
      )}

      {/* Action buttons — only shown when items are still outstanding */}
      {!allActioned && canApprove && outstanding.length > 0 && (
        <div className="import-preview-card__actions">
          <button
            className="import-preview-card__btn import-preview-card__btn--accept-all"
            onClick={handleAcceptAll}
            disabled={isAcceptingAll}
            type="button"
            aria-label={`Accept all ${outstanding.length} outstanding import suggestions`}
          >
            {isAcceptingAll
              ? 'Accepting…'
              : `Accept All (${outstanding.length})`}
          </button>
          <button
            className={`import-preview-card__btn import-preview-card__btn--review ${reviewMode ? 'import-preview-card__btn--active' : ''}`}
            onClick={() => setReviewMode((v) => !v)}
            type="button"
            aria-expanded={reviewMode}
          >
            {reviewMode ? 'Hide Items' : `Review Each`}
          </button>
          <button
            className="import-preview-card__btn import-preview-card__btn--dismiss-all"
            onClick={handleDismissAll}
            disabled={isDismissingAll}
            type="button"
            aria-label={`Dismiss all ${outstanding.length} outstanding import suggestions`}
          >
            {isDismissingAll ? 'Dismissing…' : 'Dismiss All'}
          </button>
        </div>
      )}

      {/* Read-only notice for shared trip viewers */}
      {!canApprove && outstanding.length > 0 && (
        <div className="import-preview-card__readonly-notice">
          Only the trip owner can accept or dismiss these import suggestions.
        </div>
      )}

      {/* Review each — scrollable list of ProposalCards */}
      {reviewMode && batchSuggestions.length > 0 && (
        <div className="import-preview-card__review-list">
          {batchSuggestions.map((suggestion) => (
            <div key={suggestion.id} className="import-preview-card__review-item">
              <span className="import-preview-card__item-entity-icon">
                {getEntityIcon(suggestion.action?.entityType)}
              </span>
              <div className="import-preview-card__item-content">
                <ProposalCard
                  suggestion={suggestion}
                  onApprove={onApprove}
                  onDismiss={onDismiss}
                  onModify={onModify}
                  canApprove={canApprove}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading state — when batchSuggestions not yet loaded */}
      {reviewMode && batchSuggestions.length === 0 && (
        <div className="import-preview-card__loading">
          Loading import items…
        </div>
      )}
    </div>
  );
};

export default ImportPreviewCard;
